'use client';

import { c as _c } from "react/compiler-runtime";
import { useState } from 'react';
import Link from 'next/link';
import { Store } from 'lucide-react';
import { useSession } from '@/lib/auth-client';
import { cn } from '@/lib/utils';
import { useDismissible } from '@/lib/hooks/useDismissible';
import { DirectoryListingDialog } from './DirectoryListingDialog';

/** LocalSettings key the directory callout uses to remember dismissal. */
const CALLOUT_DISMISS_KEY = 'directoryListingCallout.dismissed';
interface DirectoryListingButtonProps {
  /** Button label. */
  label?: string;
  /** Where signed-out users are sent to sign up. */
  signInHref?: string;
  /**
   * LocalSettings key of the related callout. When the callout is dismissed
   * the button shows for everyone so there is still an entry point.
   */
  dismissKey?: string;
  className?: string;
}

/**
 * Compact "Get listed" button/badge that opens the {@link DirectoryListingDialog}
 * for signed-in users (or links to sign up when signed out).
 *
 * Visibility: shown when the user has a session, or when the related directory
 * callout has been dismissed (so a dismissed callout still leaves a way in).
 */
export function DirectoryListingButton(t0) {
  const $ = _c(17);
  const {
    label: t1,
    signInHref: t2,
    dismissKey: t3,
    className
  } = t0;
  const label = t1 === undefined ? "Get listed" : t1;
  const signInHref = t2 === undefined ? "/auth/signin?redirect=/directory/get-listed" : t2;
  const dismissKey = t3 === undefined ? CALLOUT_DISMISS_KEY : t3;
  const {
    data: session
  } = useSession();
  const isLoggedIn = !!session?.user;
  const [dialogOpen, setDialogOpen] = useState(false);
  const {
    isDismissed,
    hasLoaded
  } = useDismissible(dismissKey);
  const shouldShow = isLoggedIn || hasLoaded && isDismissed;
  if (!shouldShow) {
    return null;
  }
  let t4;
  if ($[0] !== className) {
    t4 = cn("inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-full bg-gray-900 px-4 py-1.5 text-sm font-bold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md", className);
    $[0] = className;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const buttonClass = t4;
  if (!isLoggedIn) {
    let t5;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Store className="h-4 w-4" aria-hidden="true" />;
      $[2] = t5;
    } else {
      t5 = $[2];
    }
    let t6;
    if ($[3] !== buttonClass || $[4] !== label || $[5] !== signInHref) {
      t6 = <Link href={signInHref} className={buttonClass}>{t5}{label}</Link>;
      $[3] = buttonClass;
      $[4] = label;
      $[5] = signInHref;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    return t6;
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => setDialogOpen(true);
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Store className="h-4 w-4" aria-hidden="true" />;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== buttonClass || $[10] !== label) {
    t7 = <button type="button" onClick={t5} className={buttonClass}>{t6}{label}</button>;
    $[9] = buttonClass;
    $[10] = label;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== dialogOpen) {
    t8 = <DirectoryListingDialog open={dialogOpen} onOpenChange={setDialogOpen} />;
    $[12] = dialogOpen;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== t7 || $[15] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  return t9;
}